import { HTMLAttributes } from 'react';

type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'card';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export default function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  className = '',
  ...props
}: SkeletonProps) {
  const baseClasses = 'skeleton-shimmer animate-pulse';

  const variantClasses: Record<SkeletonVariant, string> = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
    card: 'rounded-xl',
  };

  const style = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`} {...props}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={`${baseClasses} ${variantClasses.text}`}
            style={{
              ...style,
              width: i === lines - 1 ? '75%' : width || '100%',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
      {...props}
    />
  );
}

export function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-3">
          <Skeleton width={96} height={16} />
          <Skeleton width={80} height={32} />
          <Skeleton width={128} height={20} variant="rectangular" />
        </div>
        <Skeleton variant="rectangular" width={48} height={48} className="rounded-xl" />
      </div>
    </div>
  );
}

export function FarmCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card overflow-hidden">
      <Skeleton variant="rectangular" height={160} className="rounded-none" />
      <div className="p-4 space-y-3">
        <Skeleton width="60%" height={20} />
        <Skeleton width="40%" height={16} />
        <div className="flex gap-2 pt-2">
          <Skeleton variant="rectangular" width={64} height={24} className="rounded-full" />
          <Skeleton variant="rectangular" width={80} height={24} className="rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function TableRowSkeleton({ columns = 4 }: { columns?: number }) {
  return (
    <tr>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-6 py-4">
          <Skeleton
            width={i === 0 ? '80%' : i === columns - 1 ? 64 : '60%'}
            height={16}
          />
        </td>
      ))}
    </tr>
  );
}

export function ChartSkeleton({ height = 256 }: { height?: number }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton width={160} height={24} />
        <Skeleton width={100} height={32} variant="rectangular" className="rounded-lg" />
      </div>
      <Skeleton variant="rectangular" height={height} />
    </div>
  );
}

export function ListSkeleton({ items = 5 }: { items?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50">
          <Skeleton variant="circular" width={40} height={40} />
          <div className="flex-1 space-y-2">
            <Skeleton width="50%" height={16} />
            <Skeleton width="30%" height={14} />
          </div>
          <Skeleton width={64} height={24} variant="rectangular" className="rounded-full" />
        </div>
      ))}
    </div>
  );
}
